'use client'

// The piano, heard.
//
// Renders nothing. When the sim says he is at the keys (npc.action
// PIANO), this starts the generated piano at the tier his streamed skill
// has earned, and mixes it by the camera's distance to the instrument
// every frame: far away he is a feeling on the wind, up close he is a
// bird working through a line. Stopping is as important as starting; a
// piano that plays to an empty room after he walks off would undo the
// whole trick.
//
// Reads the piano's position from the streamed entity (which exists only
// when Sam has placed the POI and flipped pianoEnabled), so this mounts
// safely today and lights up the day the instrument lands.

import { useEffect, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import useGameStore from '../../../stores/gameStore'
import { startPiano, startPiece, stopPiano, setPianoDistance, isPianoPlaying } from '../../../lib/audio/piano'

// Mirror of the sim's tier thresholds. Two numbers and two names per
// side; a shared module across the trees would cost more than the risk.
function tierOf(skill) {
  if (skill >= 0.7) return 'tunes'
  if (skill >= 0.45) return 'pieces'
  if (skill >= 0.2) return 'picking'
  return 'plinking'
}

export function PianoAudio() {
  const npcStates = useGameStore((s) => s.npcStates)
  const entityStates = useGameStore((s) => s.entityStates)
  const { camera } = useThree()
  const pianoRef = useRef(null)

  const piano = entityStates?.find?.((e) => e.id === 'activity_piano') || null
  const bird = npcStates?.[0] || null
  const atKeys = !!piano && String(bird?.action || '').toUpperCase() === 'PIANO'

  // A piece he wrote rides the entity from the pieces stage up (sim
  // world/Pieces.js); below that the generator is what learning sounds like.
  const piece = piano?.state?.piece || null
  const pieceId = piece?.id || null
  useEffect(() => {
    if (atKeys && piece?.events?.length) {
      startPiece(piece)
    } else if (atKeys && !isPianoPlaying()) {
      const skill = Number(piano?.state?.skill) || 0
      // Seeded per sitting so a session is one improvisation, not a
      // shuffle: the timesUsed counter is the natural session id.
      startPiano(tierOf(skill), (piano?.state?.timesUsed || 0) + 1)
    }
    if (!atKeys && isPianoPlaying()) stopPiano()
    return () => { if (isPianoPlaying()) stopPiano() }
  }, [atKeys, piano?.state?.skill, piano?.state?.timesUsed, pieceId]) // eslint-disable-line react-hooks/exhaustive-deps

  useFrame(() => {
    if (!isPianoPlaying()) return
    const p = pianoRef.current || piano?.pos
    if (!p) return
    pianoRef.current = p
    const dx = camera.position.x - p.x
    const dy = camera.position.y - (p.y || 0)
    const dz = camera.position.z - p.z
    setPianoDistance(Math.sqrt(dx * dx + dy * dy + dz * dz))
  })

  return null
}
